<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAsyncData } from '@/hooks/async-data';
import { $fetch } from '@/plugins/fetch';
import CaseCard from './components/CaseCard.vue';
import Pagination from '@/components/Pagination.vue';
import type {
  PublicCaseStudyPageData,
  SelectorOption,
  PublicCaseStudyListRequest,
} from './types';

const { t } = useI18n();

const listRequestParams = ref<PublicCaseStudyListRequest>({
  industryId: '',
  projectId: '',
  name: '',
  page: 1,
  pageSize: 12,
});

const { data: listData, pending: listPending } = useAsyncData(
  'case-study-list',
  () => $fetch<PublicCaseStudyPageData>('/case/research/v1/study/list', {
    data: {
      industryId: listRequestParams.value.industryId || undefined,
      projectId: listRequestParams.value.projectId || undefined,
      name: listRequestParams.value.name || undefined,
      page: listRequestParams.value.page,
      pageSize: listRequestParams.value.pageSize,
      isRecommend: -1,
    },
  }),
  {
    watch: [() => listRequestParams.value.industryId, () => listRequestParams.value.projectId, () => listRequestParams.value.name, () => listRequestParams.value.page],
  },
);

const { data: industryOptions } = useAsyncData(
  'case-study-industry-selector',
  () => $fetch<SelectorOption[]>('/case/research/v1/industry/selector', { data: {} }),
);

const { data: projectOptions } = useAsyncData(
  'case-study-project-selector',
  () => $fetch<SelectorOption[]>('/case/research/v1/project/selector', { data: {} }),
);

const industryList = computed(() => {
  const options: SelectorOption[] = industryOptions.value || [];
  return [
    { label: t('caseStudy.filter.all'), value: '' },
    ...options.map((item: SelectorOption) => ({ label: item.label, value: item.value })),
  ];
});

const technologyList = computed(() => {
  const options: SelectorOption[] = projectOptions.value || [];
  return [
    { label: t('caseStudy.filter.all'), value: '' },
    ...options.map((item: SelectorOption) => ({ label: item.label, value: item.value })),
  ];
});

const caseList = computed(() => listData.value?.list || []);

const totalCount = computed(() => listData.value?.totalCount || 0);

const pager = computed(() => ({
  offset: (listRequestParams.value.page - 1) * listRequestParams.value.pageSize,
  total: totalCount.value,
}));

const searchKeyword = ref('');

const handleIndustryChange = (value: string) => {
  listRequestParams.value.industryId = value;
  listRequestParams.value.page = 1;
};

const handleTechnologyChange = (value: string) => {
  listRequestParams.value.projectId = value;
  listRequestParams.value.page = 1;
};

const handleSearch = () => {
  listRequestParams.value.name = searchKeyword.value.trim();
  listRequestParams.value.page = 1;
};

const handleSearchKeyup = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    handleSearch();
  }
};

const handlePageChange = (offset: number) => {
  listRequestParams.value.page = Math.floor(offset / listRequestParams.value.pageSize) + 1;
  window.scrollTo({ top: 400, behavior: 'smooth' });
};
</script>

<template>
  <main class="page-case-study bg-white pb-96 <md:pb-48">
    <section class="case-study-hero h-400 bg-[url(@/assets/project/banner.jpg)] bg-cover bg-center bg-no-repeat <md:h-240 <md:flex <md:items-center <md:bg-[linear-gradient(160deg,#eaf2ff,#f5f7fa)] <md:text-center">
      <div class="container pt-143 <md:py-0 <sm:px-16 <xl:px-24">
        <h1 class="m-0 text-50 color-#2d2d2d font-medium <md:text-32">
          {{ t('caseStudy.title') }}
        </h1>
        <p class="mt-18 max-w-900 text-24 color-#2d2d2d leading-36 <2xl:max-w-600 <md:max-w-full <md:text-16 <md:leading-26">
          {{ t('caseStudy.description') }}
        </p>
      </div>
    </section>

    <section class="container mt-32 <sm:px-16 <xl:px-24">
      <div class="flex flex-wrap items-center gap-16">
        <span class="text-14 color-#9A9EA6">{{ t('caseStudy.filter.industry.label') }}:</span>
        <div class="flex flex-wrap gap-12">
          <button
            v-for="item in industryList"
            :key="item.value"
            :class="[
              'cursor-pointer rounded-4 border-none px-16 py-8 text-14 transition-all',
              listRequestParams.industryId === item.value
                ? 'bg-brand-primary color-white'
                : 'bg-[rgba(45,45,45,0.04)] color-#2d2d2d hover:bg-[rgba(45,45,45,0.08)]',
            ]"
            @click="handleIndustryChange(item.value)"
          >
            {{ item.label }}
          </button>
        </div>
      </div>

      <div class="mt-16 flex flex-wrap items-center gap-16">
        <span class="text-14 color-#9A9EA6">{{ t('caseStudy.filter.technology.label') }}:</span>
        <div class="flex flex-wrap gap-12">
          <button
            v-for="item in technologyList"
            :key="item.value"
            :class="[
              'cursor-pointer rounded-4 border-none px-16 py-8 text-14 transition-all',
              listRequestParams.projectId === item.value
                ? 'bg-brand-primary color-white'
                : 'bg-[rgba(45,45,45,0.04)] color-#2d2d2d hover:bg-[rgba(45,45,45,0.08)]',
            ]"
            @click="handleTechnologyChange(item.value)"
          >
            {{ item.label }}
          </button>
        </div>
      </div>

      <div class="mt-24 flex items-center gap-12 <md:flex-wrap">
        <div class="w-400 <md:w-full">
          <input
            v-model="searchKeyword"
            type="text"
            :placeholder="t('caseStudy.filter.searchPlaceholder')"
            class="h-44 w-full border border-[rgba(154,158,166,0.2)] rounded-4 bg-white px-16 pr-44 text-14 color-#2d2d2d outline-none focus:border-brand-primary"
            @keyup="handleSearchKeyup"
          >
        </div>
        <button
          class="h-44 cursor-pointer rounded-4 border-none bg-brand-primary px-24 text-14 color-white transition-opacity hover:opacity-80"
          @click="handleSearch"
        >
          {{ t('caseStudy.search') }}
        </button>
      </div>
    </section>

    <section class="container mt-32 <sm:px-16 <xl:px-24">
      <ul v-if="caseList.length > 0" class="grid grid-cols-4 m-0 gap-24 p-0 <lg:grid-cols-2 <sm:grid-cols-1 <xl:grid-cols-3 <xl:gap-16">
        <CaseCard
          v-for="item in caseList"
          :key="item.id"
          :item="item"
        />
      </ul>

      <div v-else-if="!listPending" class="h-300 flex-center text-16 color-#9A9EA6">
        {{ t('caseStudy.empty') }}
      </div>

      <div v-if="listPending" class="h-300 flex-center">
        <span class="i-home-icons:loading size-32 animate-spin color-#9A9EA6"></span>
      </div>
    </section>

    <section class="container mt-48 <sm:px-16 <xl:px-24">
      <Pagination
        v-if="totalCount > listRequestParams.pageSize"
        :pager="pager"
        :limit="listRequestParams.pageSize"
        @change="handlePageChange"
      />

      <p class="m-0 text-center text-16 color-#9A9EA6" :class="{ 'mt-32': totalCount > listRequestParams.pageSize }">
        {{ t('caseStudy.keepUpdating') }}
      </p>
    </section>
  </main>
</template>

<style lang="scss" scoped>
</style>
